'use client';
import {
  IconFolderExclamation,
  IconFolderOff,
  IconLayoutGrid,
  IconLayoutList,
} from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { Button, DataTable, cn } from 'ui';
import { getHumanFileSize, localeDateFormat } from 'utils';
import { FileCardIcon, FileCardPreview, getFileType } from './FileCard';
import { FilePreviewDialog } from './FilePreviewDialog';

// Generic file viewer with two modes (grid | list). Takes a normalized array
// (`FilesDisplayerItem`) so it stays decoupled from any particular contract.
export interface FilesDisplayerItem {
  id: string;
  name: string;
  size: number | string;
  mimetype: string | null;
  url: string;
  previewURL?: string;
  // Display name of whoever uploaded the file (optional).
  uploadedBy?: string | null;
  // Upload date (optional).
  uploadedAt?: string | Date | null;
}

type FilesDisplayerView = 'grid' | 'list';

const humanSize = (size: number | string): string =>
  getHumanFileSize(Number(size), true);

const ViewToggle = ({
  view,
  onChange,
}: {
  view: FilesDisplayerView;
  onChange: (view: FilesDisplayerView) => void;
}) => (
  <div className='inline-flex items-center gap-1 mr-3'>
    <Button
      type='button'
      size='icon'
      variant='ghost'
      aria-label='Vista de cuadrícula'
      aria-pressed={view === 'grid'}
      className={cn(
        'h-7 w-7',
        view === 'grid' && 'bg-accent text-accent-foreground',
      )}
      onClick={() => onChange('grid')}
    >
      <IconLayoutGrid className='h-4 w-4' />
    </Button>
    <Button
      type='button'
      size='icon'
      variant='ghost'
      aria-label='Vista de lista'
      aria-pressed={view === 'list'}
      className={cn(
        'h-7 w-7',
        view === 'list' && 'bg-accent text-accent-foreground',
      )}
      onClick={() => onChange('list')}
    >
      <IconLayoutList className='h-4 w-4' />
    </Button>
  </div>
);

const FilePreviewThumbnail = ({ file }: { file: FilesDisplayerItem }) => {
  const type = getFileType(file.mimetype);

  // Only images and videos make a useful thumbnail; everything else falls back
  // to the file type icon.
  if (type === 'image' || type === 'video') {
    return (
      <FileCardPreview
        type={type}
        previewURL={file.previewURL || file.url}
        mode='thumbnail'
        className='h-full w-full'
        imageClassName='h-full w-full object-cover'
      />
    );
  }

  return (
    <div className='flex h-full w-full items-center justify-center'>
      <FileCardIcon className='h-10 w-10' mimetype={file.mimetype || ''} />
    </div>
  );
};

const FileMeta = ({ file }: { file: FilesDisplayerItem }) => {
  const hasAuthor = !!file.uploadedBy;
  const hasDate = !!file.uploadedAt;

  if (!hasAuthor && !hasDate) return null;

  return (
    <span className='truncate text-xs text-muted-foreground'>
      {file.uploadedBy}
      {hasAuthor && hasDate ? ' - ' : ''}
      {hasDate ? localeDateFormat(file.uploadedAt!) : ''}
    </span>
  );
};

const FilesGrid = ({
  files,
  onOpenFile,
  renderItemActions,
}: {
  files: FilesDisplayerItem[];
  onOpenFile: (fileId: string) => void;
  renderItemActions?: (file: FilesDisplayerItem) => React.ReactNode;
}) => (
  <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5'>
    {files.map((file) => (
      // The card is a container, not a button: nesting the actions inside the
      // preview button would be invalid markup.
      <div
        key={file.id}
        className='group relative flex flex-col overflow-hidden rounded-xl border bg-white transition hover:shadow-sm'
      >
        {renderItemActions && (
          <div className='absolute right-1 top-1 z-10'>
            {renderItemActions(file)}
          </div>
        )}
        <button
          type='button'
          onClick={() => onOpenFile(file.id)}
          className='flex flex-col text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
        >
          <div className='flex h-36 w-full items-center justify-center overflow-hidden bg-slate-50'>
            <FilePreviewThumbnail file={file} />
          </div>
          <div className='flex flex-col gap-0.5 p-2'>
            <div className='flex items-center'>
              <FileCardIcon
                className='h-3.5 w-3.5 shrink-0'
                mimetype={file.mimetype || ''}
              />

              <span className='min-w-0 flex-1 truncate text-[13px] font-medium group-hover:underline'>
                {file.name}
              </span>

              <span className='shrink-0 text-[11px] text-muted-foreground'>
                ({humanSize(file.size)})
              </span>
            </div>
            <FileMeta file={file} />
          </div>
        </button>
      </div>
    ))}
  </div>
);

const FilesList = ({
  files,
  onOpenFile,
  renderItemActions,
}: {
  files: FilesDisplayerItem[];
  onOpenFile: (fileId: string) => void;
  renderItemActions?: (file: FilesDisplayerItem) => React.ReactNode;
}) => {
  const columns = useMemo(
    (): ColumnDef<FilesDisplayerItem>[] => [
      {
        id: 'name',
        header: 'Nombre',
        cell: ({ row }) => (
          <button
            type='button'
            onClick={() => onOpenFile(row.original.id)}
            className='group flex items-center gap-1 w-[200px] text-left'
          >
            <FileCardIcon
              className='h-3.5 w-3.5'
              mimetype={row.original.mimetype || ''}
            />
            <span className='text-sm group-hover:underline max-w-[200px] truncate'>
              {row.original.name}
            </span>
          </button>
        ),
      },
      {
        id: 'size',
        header: 'Peso',
        meta: { widthClassName: 'w-[120px]' },
        cell: ({ row }) => (
          <span className='text-sm text-muted-foreground w-[75px]'>
            {humanSize(row.original.size)}
          </span>
        ),
      },
      {
        id: 'uploaded_by',
        header: 'Subido por',
        meta: { widthClassName: 'w-[250px]' },
        cell: ({ row }) => (
          <span className='text-sm text-muted-foreground w-[150px]'>
            {row.original.uploadedBy || '—'}
          </span>
        ),
      },
      {
        id: 'uploaded_at',
        header: 'Fecha de subida',
        meta: { widthClassName: 'w-[160px]' },
        cell: ({ row }) => (
          <span className='text-sm text-muted-foreground w-[150px]'>
            {row.original.uploadedAt
              ? localeDateFormat(row.original.uploadedAt)
              : '—'}
          </span>
        ),
      },
      ...(renderItemActions
        ? [
            {
              id: 'actions',
              header: '',
              meta: { widthClassName: 'w-[50px]', align: 'center' },
              cell: ({ row }) => renderItemActions(row.original),
            } satisfies ColumnDef<FilesDisplayerItem>,
          ]
        : []),
    ],
    [onOpenFile, renderItemActions],
  );

  return <DataTable columns={columns} data={files} align='left' compact />;
};

export const FilesDisplayer = ({
  files,
  title = 'Documentos',
  maxHeight = '26rem',
  defaultView = 'grid',
  emptyMessage = 'No se encontraron documentos.',
  action,
  renderItemActions,
  onItemClick,
}: {
  files: FilesDisplayerItem[];
  title?: string;
  // Max height of the content area; overflow scrolls vertically.
  maxHeight?: string;
  defaultView?: FilesDisplayerView;
  emptyMessage?: string;
  // Optional slot on the right of the header (e.g. an upload button).
  action?: React.ReactNode;
  // Optional per-file slot: a remove button, a selection checkbox, whatever the
  // caller needs. Omitted = read-only list.
  renderItemActions?: (file: FilesDisplayerItem) => React.ReactNode;
  // Overrides the click on a file. With it the preview dialog is not rendered at
  // all, which is what lets this run inside another dialog (e.g. a picker).
  onItemClick?: (file: FilesDisplayerItem) => void;
}) => {
  const [view, setView] = useState<FilesDisplayerView>(defaultView);
  // The viewer is tracked by id, not index: it survives the list being
  // reordered or refetched while open.
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const onFileClick = useCallback(
    (fileId: string) => {
      if (!onItemClick) return setPreviewFileId(fileId);

      const file = files.find((item) => item.id === fileId);
      if (file) onItemClick(file);
    },
    [files, onItemClick],
  );

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-col lg:flex-row items-start lg:items-center justify-between gap-2 mb-1'>
        <h4 className='text-sm font-semibold text-foreground'>{title}</h4>
        <div className='flex items-center gap-3'>
          {action}
          {files.length > 0 && <ViewToggle view={view} onChange={setView} />}
        </div>
      </div>

      {files.length > 0 ? (
        <div
          className='overflow-y-auto'
          // `scrollbar-gutter: stable` always reserves the scrollbar width, so
          // two stacked FilesDisplayers keep the same width and stay aligned.
          style={{ maxHeight, scrollbarGutter: 'stable' }}
        >
          {view === 'grid' ? (
            <FilesGrid
              files={files}
              onOpenFile={onFileClick}
              renderItemActions={renderItemActions}
            />
          ) : (
            <FilesList
              files={files}
              onOpenFile={onFileClick}
              renderItemActions={renderItemActions}
            />
          )}
        </div>
      ) : (
        <div className='text-center text-sm text-muted-foreground flex items-center justify-center gap-2 py-5'>
          <IconFolderExclamation className='w-4 h-4' />
          <span>{emptyMessage}</span>
        </div>
      )}

      {!onItemClick && (
        <FilePreviewDialog
          files={files}
          activeFileId={previewFileId}
          onActiveFileIdChange={setPreviewFileId}
        />
      )}
    </div>
  );
};
